import { Head } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import AmbassadorSignInPanel from "@/components/auth/ambassador-sign-in-panel"
import BrandSignInPanel from "@/components/auth/brand-sign-in-panel"
import RoleGate, { type LoginRole } from "@/components/auth/role-gate"
import AuthLoginLayout from "@/layouts/auth-login-layout"

/**
 * Read `return_to` from the URL query string at render time. Used by the
 * OKS-24 Variant B recognised-email flow (UX Spec §12.2 + QA TRD §C29):
 * the public landing's "Sign in to continue" CTA carries
 * `?return_to=/i/:token`; after a successful POST /sign_in the backend
 * redirects there (allowlisted in `SessionsController#create`) so the
 * landing can re-resolve under authenticated context and flip to
 * Variant A.
 *
 * Server doesn't ship `return_to` as an Inertia prop — the public
 * landing carries it via the browser URL and the Brand panel forwards
 * it as a hidden form field. SSR-safe (returns null when `window` is
 * undefined).
 */
function readReturnTo(): string | null {
  if (typeof window === "undefined") return null
  return new URLSearchParams(window.location.search).get("return_to")
}

/**
 * Login — OKS-475 role-gated returning-login surface (`/sign_in`).
 *
 * One canonical URL with three client-side panels (no new routes, no
 * reload): a Role-select gate first, then the Brand (staff
 * email/password) or Ambassador (TikTok) branch. Asking the role first
 * means a Brand never sees the TikTok button and a creator never sees a
 * password field. `return_to` is read once at the top and threaded into
 * the Brand panel so the staff path is byte-for-byte preserved. Back
 * links swap panels instantly via local state.
 */
export default function Login() {
  const { t } = useTranslation()
  const [role, setRole] = useState<LoginRole | null>(null)
  const returnTo = readReturnTo()

  return (
    <AuthLoginLayout>
      <Head title={t("login.role_select.title")} />
      {role === null && <RoleGate onSelect={setRole} />}
      {role === "brand" && (
        <BrandSignInPanel returnTo={returnTo} onBack={() => setRole(null)} />
      )}
      {role === "ambassador" && (
        <AmbassadorSignInPanel onBack={() => setRole(null)} />
      )}
    </AuthLoginLayout>
  )
}
